import { Link } from "@tanstack/react-router";
import { ClipboardList, Plus } from "lucide-react";
import { useState } from "react";
import {
  useApproveRequest,
  useCancelRequest,
  useCreateRefillRequest,
  useDeclineRequest,
  useRefillRequests,
  useSites,
} from "@/api/hooks";
import { ApiError } from "@/api/problem";
import type { RefillRequest, RefillRequestStatus } from "@/api/types";
import { useSession } from "@/auth/session";
import { PageHeader } from "@/components/app-shell";
import { Dialog } from "@/components/ui/dialog";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  MicroLabel,
  Num,
  NumberInput,
  Textarea,
} from "@/components/ui/primitives";
import { NONE, Select } from "@/components/ui/select";
import { EmptyState, ErrorState, FormError, LoadingRows } from "@/components/ui/states";
import { formatDate, formatDateTime, formatLiters } from "@/lib/format";
import { useI18n } from "@/i18n/provider";

/**
 * Refill requests: an operator asks, an admin decides.
 *
 * The two roles see the same screen and a different set of buttons — which is the whole
 * point of the feature, and exactly the thing that must not be got wrong. An operator who
 * can see an Approve button will press it, and be told 403 by a server that was right.
 */

const STATUS_TONE: Record<RefillRequestStatus, "low" | "ok" | "dry" | "idle" | "ink"> = {
  pending: "low",
  approved: "ok",
  declined: "dry",
  fulfilled: "ink",
  cancelled: "idle",
};

export function RequestsPage() {
  const { t } = useI18n();
  const { can } = useSession();

  const [status, setStatus] = useState<string>("");
  const [creating, setCreating] = useState(false);

  const requestsQuery = useRefillRequests(
    status ? { status: status as RefillRequestStatus } : {},
  );

  const statusOptions = [
    { value: NONE, label: t("common.all") },
    { value: "pending", label: t("requests.status.pending") },
    { value: "approved", label: t("requests.status.approved") },
    { value: "declined", label: t("requests.status.declined") },
    { value: "fulfilled", label: t("requests.status.fulfilled") },
    { value: "cancelled", label: t("requests.status.cancelled") },
  ];

  const requests = requestsQuery.data?.data ?? [];

  return (
    <>
      <PageHeader
        title={t("requests.title")}
        actions={
          can("requests:create") ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              {t("requests.new")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader>
          <Field label={t("refills.status")} className="w-52">
            {(props) => (
              <Select
                {...props}
                value={status || NONE}
                onValueChange={(value) => setStatus(value === NONE ? "" : value)}
                options={statusOptions}
                placeholder={t("common.all")}
              />
            )}
          </Field>
        </CardHeader>

        {requestsQuery.isPending ? (
          <LoadingRows />
        ) : requestsQuery.error ? (
          <ErrorState error={requestsQuery.error} onRetry={() => void requestsQuery.refetch()} />
        ) : requests.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="size-6" />}
            title={t("requests.empty")}
            body={t("requests.emptyBody")}
            action={
              can("requests:create") ? (
                <Button size="sm" onClick={() => setCreating(true)}>
                  {t("requests.new")}
                </Button>
              ) : null
            }
          />
        ) : (
          <CardBody className="flex flex-col gap-3">
            {requests.map((request) => (
              <RequestCard key={request.id} request={request} />
            ))}
          </CardBody>
        )}
      </Card>

      {/* Mounted only while open — a fresh, empty form every time it is raised. */}
      {creating ? <CreateRequestDialog open onOpenChange={setCreating} /> : null}
    </>
  );
}

function RequestCard({ request }: { request: RefillRequest }) {
  const { t, lang } = useI18n();
  const { can, me } = useSession();
  const cancel = useCancelRequest();

  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);

  const isMine = request.requested_by.user_id === me?.user.id;
  const isPending = request.status === "pending";

  // The decision buttons belong to owners and admins. An operator sees the row, its status
  // and its outcome — and no controls that would 403.
  const canDecide = can("requests:decide") && isPending;
  const canWithdraw = isPending && (isMine || can("requests:decide"));

  return (
    <div className="rounded-[6px] border border-rule p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/sites/$siteId"
              params={{ siteId: request.site.id }}
              className="text-sm font-semibold text-text weight-display underline-offset-4 hover:underline"
            >
              {request.site.name}
            </Link>
            <Badge tone={STATUS_TONE[request.status]}>
              {t(`requests.status.${request.status}` as const)}
            </Badge>
          </div>

          <p className="mt-1 text-xs text-faint">
            {t("requests.requestedBy")}: {request.requested_by.name} ·{" "}
            <Num>{formatDateTime(lang, request.created_at)}</Num>
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-5">
          <div className="text-end">
            <MicroLabel>{t("requests.requested")}</MicroLabel>
            <p className="font-mono text-lg text-text">
              <Num>{formatLiters(lang, request.requested_liters)}</Num>
            </p>
          </div>

          {request.approved_liters !== undefined ? (
            <div className="text-end">
              <MicroLabel>{t("requests.approved")}</MicroLabel>
              <p
                className={
                  "font-mono text-lg " +
                  // An approval for less than was asked for is the number the operator most
                  // needs to see. It must not look like the number they requested.
                  (request.approved_liters < request.requested_liters
                    ? "font-semibold text-low"
                    : "text-ok")
                }
              >
                <Num>{formatLiters(lang, request.approved_liters)}</Num>
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {request.needed_by ? (
        <p className="mt-2 text-xs text-muted">
          {t("requests.neededBy")}: <Num>{formatDate(lang, request.needed_by)}</Num>
        </p>
      ) : null}

      {request.notes ? (
        <p className="mt-2 rounded-[4px] bg-paper-sunk p-2 text-xs text-muted">{request.notes}</p>
      ) : null}

      {request.decision_note ? (
        <p className="mt-2 border-s-2 border-rule-strong ps-2 text-xs text-muted">
          <span className="font-medium text-text">
            {request.decided_by?.name ?? t("requests.decidedBy")}:
          </span>{" "}
          {request.decision_note}
        </p>
      ) : null}

      {canDecide || canWithdraw || request.status === "approved" ? (
        <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-rule pt-3">
          {canWithdraw ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate(request.id)}
            >
              {t("requests.cancel")}
            </Button>
          ) : null}

          {canDecide ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setDeclining(true)}>
                {t("requests.decline")}
              </Button>
              <Button size="sm" onClick={() => setApproving(true)}>
                {t("requests.approve")}
              </Button>
            </>
          ) : null}

          {/* An approved request is not the end — somebody still has to put fuel in a tank. */}
          {request.status === "approved" && can("refills:create") ? (
            <Button asChild size="sm">
              <Link to="/refills/new" search={{ siteId: request.site.id }}>
                {t("requests.fulfil")}
              </Link>
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Mounted ONLY while open, and that is load-bearing.
          A permanently-mounted dialog runs its `useState` once, at the card's first render, and
          freezes there: `ApproveDialog` would seed its litres box from whatever the request said
          back then and never re-seed it, so an approval could go out for a stale number — and a
          decline note typed once, then cancelled, would still be sitting there on the next
          request the admin opened. Mounting on open gives every open a fresh, correct form. */}
      {approving ? (
        <ApproveDialog request={request} open onOpenChange={setApproving} />
      ) : null}
      {declining ? (
        <DeclineDialog request={request} open onOpenChange={setDeclining} />
      ) : null}
    </div>
  );
}

function ApproveDialog({
  request,
  open,
  onOpenChange,
}: {
  request: RefillRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const approve = useApproveRequest();

  const [liters, setLiters] = useState(String(request.requested_liters));
  const [note, setNote] = useState("");

  const litersNumber = Number(liters);
  const valid = Number.isFinite(litersNumber) && litersNumber > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("requests.approveTitle")}
      description={request.site.name}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={approve.isPending || !valid}
            onClick={() =>
              approve.mutate(
                {
                  requestId: request.id,
                  approved_liters: litersNumber,
                  ...(note.trim() ? { note: note.trim() } : {}),
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {approve.isPending ? t("common.saving") : t("requests.approve")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError error={approve.error} />

        <Field
          label={`${t("requests.approveLiters")} (${t("common.liters")})`}
          hint={t("requests.approveHint")}
          required
        >
          {(props) => (
            <NumberInput
              {...props}
              min={1}
              value={liters}
              onChange={(event) => setLiters(event.target.value)}
            />
          )}
        </Field>

        <Field label={t("requests.decisionNote")} hint={t("common.optional")}>
          {(props) => (
            <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function DeclineDialog({
  request,
  open,
  onOpenChange,
}: {
  request: RefillRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const decline = useDeclineRequest();
  const [note, setNote] = useState("");

  const fieldErrors = decline.error instanceof ApiError ? decline.error.fieldErrors : {};

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("requests.declineTitle")}
      description={request.site.name}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          {/* The reason is mandatory — the spec says so, and it is right: an operator whose
              request vanishes with no explanation will simply raise it again tomorrow.

              NOT `danger` (which is `dry`): declining a request is a decision, not fuel running
              out. DESIGN.md §1 — `dry` means one thing, or it means nothing. */}
          <Button
            variant="secondary"
            disabled={decline.isPending || !note.trim()}
            onClick={() =>
              decline.mutate(
                { requestId: request.id, note: note.trim() },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {decline.isPending ? t("common.saving") : t("requests.decline")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError
          error={
            decline.error instanceof ApiError && decline.error.isFormLevel ? decline.error : null
          }
        />

        <Field
          label={t("requests.declineReason")}
          hint={t("requests.declineReasonHint")}
          error={fieldErrors["note"]}
          required
        >
          {(props) => (
            <Textarea
              {...props}
              required
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function CreateRequestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const createRequest = useCreateRefillRequest();
  const sitesQuery = useSites({ status: "active", limit: 100 });

  const [siteId, setSiteId] = useState("");
  const [liters, setLiters] = useState("");
  const [notes, setNotes] = useState("");

  const litersNumber = Number(liters);
  const valid = !!siteId && Number.isFinite(litersNumber) && litersNumber > 0;

  const fieldErrors =
    createRequest.error instanceof ApiError ? createRequest.error.fieldErrors : {};

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("requests.new")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!valid || createRequest.isPending}
            onClick={() =>
              createRequest.mutate(
                {
                  site_id: siteId,
                  requested_liters: litersNumber,
                  ...(notes.trim() ? { notes: notes.trim() } : {}),
                },
                {
                  onSuccess: () => {
                    onOpenChange(false);
                    setSiteId("");
                    setLiters("");
                    setNotes("");
                  },
                },
              )
            }
          >
            {createRequest.isPending ? t("common.saving") : t("requests.new")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError
          error={
            createRequest.error instanceof ApiError && createRequest.error.isFormLevel
              ? createRequest.error
              : null
          }
        />

        <Field label={t("refills.site")} error={fieldErrors["site_id"]} required>
          {(props) => (
            <Select
              {...props}
              value={siteId}
              onValueChange={setSiteId}
              options={(sitesQuery.data?.data ?? []).map((site) => ({
                value: site.id,
                label: site.name,
              }))}
              placeholder={t("refills.selectSite")}
            />
          )}
        </Field>

        <Field
          label={`${t("requests.requested")} (${t("common.liters")})`}
          error={fieldErrors["requested_liters"]}
          required
        >
          {(props) => (
            <NumberInput
              {...props}
              min={1}
              value={liters}
              onChange={(event) => setLiters(event.target.value)}
            />
          )}
        </Field>

        <Field label={t("requests.notes")} error={fieldErrors["notes"]} hint={t("common.optional")}>
          {(props) => (
            <Textarea
              {...props}
              value={notes}
              placeholder={t("requests.notesPlaceholder")}
              onChange={(event) => setNotes(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
